import React, { useEffect, useState, useCallback } from "react";
import styled, { withTheme } from "styled-components";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import TargetInput from "./Controls/TargetInput";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  dailyTargetIds,
  getMacronutrientColor,
  kcalMultiplier,
} from "../../../modules/constants";

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  font-weight: 600 !important;
`;

const Title = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme?.blackLite};
  font-size: 14px;
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const SetDailyGoals = (props) => {
  const { onChange, editing, defaultTargets, theme } = props;

  const { t } = useTranslation();

  const [calories, setCalories] = useState("");

  const [longChainFats, setLongChainFats] = useState({
    grams: "",
    percentage: null,
  });

  const [mediumChainFats, setMediumChainFats] = useState({
    grams: "",
    percentage: null,
  });

  const [protein, setProtein] = useState({
    grams: "",
    percentage: null,
  });

  const [carbohydrates, setCarbohydrates] = useState({
    grams: "",
    percentage: null,
  });

  const [errors, setErrors] = useState({});

  const [reasonForChange, setReasonForChange] = useState("");

  const [caloriesError, setCaloriesError] = useState(null);
  const [reasonForChangeError, setReasonForChangeError] = useState(null);

  useEffect(() => {
    if (defaultTargets) {
      setCalories(defaultTargets.calories.toString());

      setLongChainFats({
        grams:
          defaultTargets.longChainFatsGrams?.toString() ||
          defaultTargets.longChainFatsValue?.toString(),
        percentage: defaultTargets.longChainFatsPercentage,
      });

      setMediumChainFats({
        grams:
          defaultTargets.mediumChainFatsGrams?.toString() ||
          defaultTargets.mediumChainFatsValue?.toString(),
        percentage: defaultTargets.mediumChainFatsPercentage,
      });

      setProtein({
        grams:
          defaultTargets.proteinGrams?.toString() ||
          defaultTargets.proteinValue?.toString(),
        percentage: defaultTargets.proteinPercentage,
      });

      setCarbohydrates({
        grams:
          defaultTargets.carbohydratesGrams?.toString() ||
          defaultTargets.carbsValue?.toString(),
        percentage:
          defaultTargets.carbohydratesPercentage ||
          defaultTargets.carbsPercentage,
      });
    }
  }, [defaultTargets]);

  const validateForm = () => {
    let hasErrorsCalories = false;
    let hasErrorsReason = false;
    let hasErrorTargets = false;

    setCaloriesError(null);
    setReasonForChangeError(null);
    if (!calories) {
      hasErrorsCalories = true;
    }

    if (
      !longChainFats.grams ||
      Number(longChainFats.grams) === 0 ||
      !mediumChainFats.grams ||
      Number(mediumChainFats.grams) === 0 ||
      !protein.grams ||
      Number(protein.grams) === 0 ||
      !carbohydrates.grams ||
      Number(carbohydrates.grams) === 0
    ) {
      hasErrorTargets = true;
    }

    if (editing && reasonForChange.replace(/\.|,/g, "").trim() === "") {
      setReasonForChangeError("Please provide a reason");
      hasErrorsReason = true;
    }

    if (editing && reasonForChange.length > 450) {
      setReasonForChangeError(
        "Please enter a reason with less than 450 characters"
      );
      hasErrorsReason = true;
    }

    onChange(!hasErrorsCalories && !hasErrorsReason && !hasErrorTargets, {
      calories: Number(calories),
      longChainFatsPercentage: longChainFats.percentage,
      longChainFatsGrams: Number(longChainFats.grams),
      mediumChainFatsPercentage: mediumChainFats.percentage,
      mediumChainFatsGrams: Number(mediumChainFats.grams),
      proteinPercentage: protein.percentage,
      proteinGrams: Number(protein.grams),
      carbohydratesPercentage: carbohydrates.percentage,
      carbohydratesGrams: Number(carbohydrates.grams),
      reasonForChange,
    });
  };

  const validateCalories = (value) => {
    setErrors({
      ...errors,
      calories: value ? undefined : "This field is required.",
    });
  };

  useEffect(() => {
    validateForm();
  }, [
    calories,
    longChainFats,
    mediumChainFats,
    protein,
    carbohydrates,
    reasonForChange,
  ]);

  const onLongChainFatsChanged = useCallback((percentage, grams) => {
    setLongChainFats({
      grams: grams,
      percentage: percentage,
    });
  }, []);

  const onMediumChainFatsChanged = useCallback((percentage, grams) => {
    setMediumChainFats({
      grams: grams,
      percentage: percentage,
    });
  }, []);

  const onProteinChanged = useCallback((percentage, grams) => {
    setProtein({
      grams: grams,
      percentage: percentage,
    });
  }, []);

  const onCarbohydratesChanged = useCallback((percentage, grams) => {
    setCarbohydrates({
      grams: grams,
      percentage: percentage,
    });
  }, []);

  return (
    <Container data-testid="setdailygoals">
      <Title>Set daily targets for this participant</Title>

      <LabelledInput
        data-testid="calories-input"
        className="mt-4"
        title="Calories"
        value={calories}
        pattern="[0-9]{0,4}"
        min="0"
        type="text"
        hasError={caloriesError !== null}
        errorMessage={caloriesError}
        onChange={(e) => e.target.validity.valid && setCalories(e.target.value)}
        onBlur={() => validateCalories(calories)}
      />
      <StyledError>
        {errors?.calories !== null && <span>{errors?.calories}</span>}
      </StyledError>
      <TargetInput
        data-testid="long-chain-fats-percentage-input"
        className="mt-3"
        title={t("appText.dietDiary.longChainFats")}
        calories={calories === "" ? "0" : calories}
        kCalGramsRelation={kcalMultiplier.lcf}
        info="* Protocol target: 10% of total calories"
        color={getMacronutrientColor(
          dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
          theme
        )}
        onChange={onLongChainFatsChanged}
      />

      <TargetInput
        data-testid="medium-chain-fats-percentage-input"
        className="mt-3"
        title={t("appText.dietDiary.mediumChainFats")}
        calories={calories === "" ? "0" : calories}
        kCalGramsRelation={kcalMultiplier.mcf}
        info="* Protocol target: Between 30% and 35% of total calories"
        color={getMacronutrientColor(
          dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
          theme
        )}
        onChange={onMediumChainFatsChanged}
      />

      <TargetInput
        data-testid="protein-percentage-input"
        className="mt-3"
        title="Protein"
        calories={calories === "" ? "0" : calories}
        kCalGramsRelation={kcalMultiplier.protein}
        info="* Protocol target: Between 10% and 15% of total calories"
        color={getMacronutrientColor(dailyTargetIds.PROTEIN_ATTR_ID, theme)}
        onChange={onProteinChanged}
      />

      <TargetInput
        data-testid="carbohydrates-percentage-input"
        className="mt-3"
        title="Carbohydrates"
        calories={calories === "" ? "0" : calories}
        kCalGramsRelation={kcalMultiplier.carbs}
        info="* Protocol target: Between 45% and 60% of total calories"
        color={getMacronutrientColor(dailyTargetIds.CARBS_ATTR_ID, theme)}
        onChange={onCarbohydratesChanged}
      />

      {editing && (
        <LabelledInput
          data-testid="reason-change-input"
          className="mt-4"
          title="Reason for change"
          value={reasonForChange}
          min="0"
          type="text"
          hasError={reasonForChangeError !== null}
          errorMessage={reasonForChangeError}
          onChange={(e) =>
            e.target.validity.valid && setReasonForChange(e.target.value)
          }
        />
      )}
    </Container>
  );
};

SetDailyGoals.defaultProps = {
  backgroundColor: null,
  validate: false,
  onValidate: null,
  editing: false,
  caloriesProp: null,
  carbohydratesGramsProp: null,
  carbohydratesPercentageProp: null,
  proteinGramsProp: null,
  proteinPercentageProp: null,
};

SetDailyGoals.propTypes = {
  backgroundColor: PropTypes.string,
  validate: PropTypes.bool,
  onValidate: PropTypes.func,
  editing: PropTypes.bool,
};

export default withTheme(SetDailyGoals);
